import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Alert
} from '@mui/material'
import PropertyCard, { type PropertyData } from '../components/PropertyCard'
import { useInfiniteContentByEmbed } from '../hooks/useInfiniteContentByEmbed'

export interface FeaturedWidgetProps {
  embedId: string
  title?: string
  subtitle?: string
  itemsPerPage?: number
  onViewDetails?: (property: PropertyData) => void
  onFavorite?: (property: PropertyData) => void
  onPageChange?: (page: number) => void
}


const FeaturedWidget: React.FC<FeaturedWidgetProps> = ({
  embedId,
  title = 'Featured Properties',
  subtitle,
  itemsPerPage = 20,
  onViewDetails,
  // onFavorite,
  onPageChange
}) => {
  const [isMobile, setIsMobile] = useState(false)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Base filters para las queries
  const baseFilters = useMemo(() => ({ broker: 'Y' }), [])

  // Usar infinite query siempre para poder cargar más páginas
  const infiniteQuery = useInfiniteContentByEmbed(
    embedId,
    undefined,
    baseFilters,
    itemsPerPage
  )

  // Obtener datos de la última página cargada
  const contentData = infiniteQuery.data?.pages?.[(infiniteQuery.data?.pages?.length ?? 1) - 1]
  const isLoadingContent = infiniteQuery.isLoading
  const contentError = infiniteQuery.error
  const hasNextPage = infiniteQuery.hasNextPage ?? false
  const fetchNextPage = infiniteQuery.fetchNextPage
  const isFetchingNextPage = infiniteQuery.isFetchingNextPage ?? false

  // Detectar si es móvil
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  // Función helper para transformar datos de API a PropertyData
  const transformToPropertyData = useCallback((dataArray: any[]): PropertyData[] => {
    if (!Array.isArray(dataArray)) return []
    
    return dataArray.map((item: any) => {
      const data = item.data || item
      return {
        id: data.id?.toString() || item.id?.toString() || '',
        image: data.photos?.[0] || data.coverImage || '',
        price: `$${(data.listPrice || data.price || 0).toLocaleString()}`,
        address: data.address || data.title || '',
        city: data.city || '',
        state: data.state || '',
        zipCode: data.postalCode || data.zip || '',
        beds: data.bedrooms || data.numberOfRooms || 0,
        baths: data.bathrooms || data.numberOfBathrooms || 0,
        squareFeet: data.area || data.size || 0,
        isFeatured: data.featured || false,
        isFavorite: false,
        url: data.url || item.url || ''
      }
    })
  }, [])

  // Obtener todas las propiedades - acumular todos los datos de todas las páginas cargadas
  const properties = useMemo(() => {
    const infiniteData = infiniteQuery.data
    if (!infiniteData?.pages) return []
    
    // Aplanar todas las páginas y transformar
    const allItems: any[] = []
    infiniteData.pages.forEach(page => {
      const pageData = page?.data?.data
      if (Array.isArray(pageData)) {
        allItems.push(...pageData)
      }
    })
    
    return transformToPropertyData(allItems)
  }, [infiniteQuery.data, transformToPropertyData])

  // Debug useEffect - después de declarar properties
  useEffect(() => {
    console.log('=== FeaturedWidget contentData ===')
    console.log('contentData:', contentData)
    console.log('isMobile:', isMobile)
    console.log('hasNextPage:', hasNextPage)
    console.log('isFetchingNextPage:', isFetchingNextPage)
    console.log('properties.length:', properties.length)
    console.log('infiniteQuery.data?.pages?.length:', infiniteQuery.data?.pages?.length)
  }, [contentData, isMobile, hasNextPage, isFetchingNextPage, properties.length, infiniteQuery.data?.pages?.length])

  // Mostrar todas las propiedades cargadas (scroll infinito en ambos modos)
  const displayedProperties = properties

  const handleViewDetails = (property: PropertyData) => {
    if (property.url) {
      window.location.href = property.url
    }
    onViewDetails?.(property)
  }

  // const handleFavorite = (property: PropertyData) => {
  //   // Toggle favorite status
  //   setProperties(prev => 
  //     prev.map(p => 
  //       p.id === property.id 
  //         ? { ...p, isFavorite: !p.isFavorite }
  //         : p
  //     )
  //   )
  //   onFavorite?.(property)
  // }

  // Scroll infinito - cargar siguiente página usando fetchNextPage de React Query
  const loadMoreProperties = useCallback(async () => {
    console.log('=== loadMoreProperties called ===')
    console.log('fetchNextPage:', !!fetchNextPage)
    console.log('isFetchingNextPage:', isFetchingNextPage)
    console.log('hasNextPage:', hasNextPage)
    
    if (!fetchNextPage) {
      console.log('❌ No fetchNextPage function available')
      return
    }
    
    if (isFetchingNextPage) {
      console.log('❌ Already fetching next page')
      return
    }
    
    if (!hasNextPage) {
      console.log('❌ No next page available')
      return
    }
    
    try {
      console.log('✅ Calling fetchNextPage()...')
      await fetchNextPage()
      console.log('✅ fetchNextPage completed')
    } catch (err) {
      console.error('❌ Error loading more properties:', err)
    }
  }, [fetchNextPage, isFetchingNextPage, hasNextPage])

  // Usar Intersection Observer para detectar cuando el final de la lista es visible
  useEffect(() => {
    const observerTarget = loadMoreRef.current
    if (!observerTarget || !hasNextPage || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          console.log('End of list is visible, loading more properties...')
          loadMoreProperties()
        }
      },
      {
        root: null, // Usar viewport como root
        rootMargin: '200px', // Cargar 200px antes de que sea visible
        threshold: 0.1
      }
    )

    observer.observe(observerTarget)

    return () => {
      if (observerTarget) {
        observer.unobserve(observerTarget)
      }
    }
  }, [hasNextPage, isFetchingNextPage, loadMoreProperties])

  // Solo mostrar loading completo en la carga inicial (cuando no hay datos todavía)
  // Una vez que tenemos datos, aunque isLoadingContent sea true (cargando otra página),
  // no mostramos el loading completo, solo el pequeño abajo
  if (isLoadingContent && properties.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    )
  }

  if (contentError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ fontSize: '1rem' }}>
          Error al cargar las propiedades: {contentError.message}
        </Alert>
      </Container>
    )
  }

  return (
    <Box
      sx={{
        backgroundColor: '#f8fafc',
        fontFamily: 'Montserrat, sans-serif',
        padding: { xs: '30px 16px 50px 16px', md: '40px 20px 60px 20px' },
        position: 'relative'
      }}
    >
      <Container maxWidth="lg">
        {/* Properties Grid */}
        <Box
          sx={{
            display: {
              xs: 'flex',
              sm: 'grid'
            },
            flexDirection: {
              xs: 'column',
              sm: 'unset'
            },
            alignItems: {
              xs: 'center',
              sm: 'unset'
            },
            justifyContent: {
              xs: 'center',
              sm: 'unset'
            },
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)'
            },
            gap: { xs: 2, md: 3 },
            marginBottom: 4,
            width: {
              xs: '100%',
              sm: '100%'
            },
            '& > *': {
              width: {
                xs: '100%',
                sm: 'auto'
              },
              maxWidth: {
                xs: '400px',
                sm: 'none'
              }
            }
          }}
        >
          {displayedProperties.map((property) => (
            <PropertyCard
              key={property.id}
              data={property}
              onViewDetails={handleViewDetails}
              //onFavorite={handleFavorite}
            />
          ))}
        </Box>

        {/* Elemento sentinela para detectar el final de la lista */}
        <Box 
          ref={loadMoreRef}
          sx={{ 
            height: '1px', 
            width: '100%',
            visibility: 'hidden'
          }}
        />

        {/* Loading indicator para infinite scroll */}
        {isFetchingNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
            <CircularProgress size={40} />
          </Box>
        )}

        {/* Mensaje cuando no hay más páginas */}
        {!isFetchingNextPage && !hasNextPage && properties.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No more properties to show
            </Typography>
          </Box>
        )}

        {/* Pagination removida - usando solo infinite scroll en ambos modos */}
      </Container>
    </Box>
  )
}

export default FeaturedWidget
